<template>
  <main>
    <md-card>
      <md-card-header>
        <div class="md-title">請求管理</div>
      </md-card-header>

      <md-card-content v-if="errorMessages && errorMessages.length > 0">
        <div class="md-layout md-gutter">
          <div class="md-layout-item">
            <flash-message :errors="errorMessages"></flash-message>
          </div>
        </div>
      </md-card-content>
      <md-card-content>
        <div class="md-layout md-gutter">
          <div class="md-layout-item md-small-size-100">
            <md-field>
              <label>請求月</label>
              <md-select name="billingMonth" id="billingMonth" v-model="form.billing_month" required
                         :disabled="sending">
                <md-option v-for="monthAndYear in selectedBillingMonth" v-bind:key="monthAndYear.key"
                           :value="monthAndYear.key">
                  {{ monthAndYear.value }}
                </md-option>
              </md-select>
            </md-field>
          </div>
          <div class="md-layout-item md-small-size-100">
            <md-autocomplete v-model="form.selectCorporateName" :md-options="corporationOptions" :disabled="sending"
                             @md-changed="getCorporationOptions" @md-opened="getCorporationOptions">
              <label>法人名</label>

              <template slot="md-autocomplete-item" slot-scope="{item}">{{ item.name }}</template>
            </md-autocomplete>
          </div>
          <div class="md-layout-item md-small-size-100"></div>
        </div>
        <div class="md-layout md-gutter">
          <div class="md-layout-item">
            <div class="md-layout">
              <div class="md-layout md-layout-item md-alignment-center-left md-size-15 md-small-size-100">
                <label>支払状況</label>
              </div>
              <div class="md-layout-item md-size-80 md-small-size-100">
                <md-checkbox v-for="(paymentStatus, index) in paymentStatuses" v-model="form.status"
                             v-bind:key="'payment-status-' + index"
                             :disabled="sending"
                             :value="paymentStatus.code">
                  {{paymentStatus.name}}
                </md-checkbox>
              </div>
            </div>
          </div>
        </div>
        <div class="md-layout md-gutter">
          <div class="md-layout-item">
            <div class="md-layout">
              <div class="md-layout md-layout-item md-alignment-center-left md-size-15 md-small-size-100">
                <label>⽀払⽅法</label>
              </div>
              <div class="md-layout-item md-size-80 md-small-size-100">
                <md-radio v-model="form.bill_method" value="" :disabled="sending">指定なし</md-radio>
                <md-radio v-for="(billingMethod, index) in checkBoxBillingMethods" v-model="form.bill_method"
                          v-bind:key="index"
                          :disabled="sending"
                          :value="index">
                  {{billingMethod}}
                </md-radio>
              </div>
            </div>
          </div>
        </div>
        <div class="md-layout md-gutter">
          <div class="md-layout-item">
            <div class="md-layout">
              <div class="md-layout md-layout-item md-alignment-center-left md-size-15 md-small-size-100">
                <label>請求書送付</label>
              </div>
              <div class="md-layout-item md-size-80 md-small-size-100">
                <md-radio v-model="form.bill_via" value="" :disabled="sending">指定なし</md-radio>
                <md-radio v-for="(billingVia, index) in checkBoxBillingVia"
                          v-model="form.bill_via"
                          v-bind:key="index"
                          :disabled="sending"
                          :value="index">
                  {{billingVia}}
                </md-radio>
                <md-button :disabled="sending" @click="searchSubmit" class="md-primary md-raised" type="submit">
                  <md-icon>search</md-icon>
                  <span>検索</span>
                </md-button>
              </div>
            </div>
          </div>
        </div>

        <loading-spinner v-if="loadBillings"></loading-spinner>
        <md-table v-else-if="pagination.total !== null">
          <md-table-toolbar>
            <h1 class="md-title">一覧</h1>
          </md-table-toolbar>
          <md-table-row>
            <md-table-head v-for="(column, key) in columns" v-bind:key="'column-key-' + key">
              <a href="#" @click="sortBy(bills, key)"
                 v-bind:class="{'active' : sortKey === key}">{{column.name}}</a>
              <md-icon v-if="sortKey === key && statusSort === 'desc'">arrow_downward</md-icon>
              <md-icon v-else-if="sortKey === key && statusSort === 'asc'">arrow_upward</md-icon>
            </md-table-head>
            <md-table-head>請求書/明細書</md-table-head>
            <md-table-head></md-table-head>
          </md-table-row>
          <md-table-row v-if="pagination.total === 0">
            <md-table-cell colspan="10">{{emptyResponseMessage}}</md-table-cell>
          </md-table-row>
          <md-table-row v-else v-for="(bill, billIndex) in bills" v-bind:key="'bill-key-' + billIndex">
            <md-table-cell>{{datetimeFormat(bill.billing_month + '01', 'YYYY年M月')}}</md-table-cell>
            <md-table-cell>{{bill.no}}</md-table-cell>
            <md-table-cell>{{bill.corporation}}</md-table-cell>
            <md-table-cell class="text-right">{{numberFormat(bill.total_billed)}}円</md-table-cell>
            <md-table-cell class="text-right">{{numberFormat(bill.total_paid)}}円</md-table-cell>
            <md-table-cell>{{paymentStatusLabelAdmin(bill.status)}}</md-table-cell>
            <md-table-cell>{{getBillingMethod(bill)}}</md-table-cell>
            <md-table-cell v-html="getPaymentInfo(bill)"></md-table-cell>
            <md-table-cell>
              <md-button :href="bill.pre_signed_url" class="md-raised" target="_blank" v-if="bill.pre_signed_url">
                <md-icon class="material-icons">
                  picture_as_pdf
                </md-icon>
              </md-button>
            </md-table-cell>
            <md-table-cell>
              <router-link :disabled="sending"
                           :to="{name: 'BackendAdminBillingDetail', params: {corporationId: bill.corporation_id.toString(), id: bill.id.toString()}}">
                <md-button class="md-raised">
                  <md-icon>description</md-icon>
                  <span>詳細</span>
                </md-button>
              </router-link>
            </md-table-cell>
          </md-table-row>
        </md-table>
        <pagination v-if="!loadBillings && bills.length" :disabled="sending"
                    :total="pagination.total"
                    :per-page="pagination.per_page"
                    :current-page="pagination.current_page"
                    @pagechanged="onPageChanged"
        />
        <div class="md-layout md-alignment-top-right" v-if="!loadBillings && bills.length">
          <md-button :disabled="sending" class="md-raised md-primary" @click="downloadAllInvoices">
            <md-icon>save_alt</md-icon>
            <span>請求書の一括ダウンロード</span>
          </md-button>
          <md-button :disabled="sending" class="md-raised md-primary" @click="downloadAllInvoicesByMail">
            <md-icon>save_alt</md-icon>
            <span>郵送する請求書をまとめてダウンロード</span>
          </md-button>
        </div>
      </md-card-content>
    </md-card>
  </main>
</template>

<script>
import Pagination                                        from '../../../components/Pagination';
import Common                                            from '../../../modules/common';
import CommonMixin                                       from '../../../mixins/commonMixin';
import {apiClientWithManagerToken as apiClient, apiList} from '../../../modules/apiClient';
import LoadingSpinner                                    from '../../../components/LoadingSpinner';
import {saveAs}                                          from 'file-saver';
import FlashMessage                                      from '../../../components/FlashMessage';
import {mapActions, mapGetters,}                         from 'vuex';

export default {
    name      : "BackendAdminBillings",
    components: {FlashMessage, LoadingSpinner, Pagination},
    mixins    : [CommonMixin],
    data      : () => ({
      sending               : false,
      emptyResponseMessage  : Common.messages.errorGet,
      pagination            : {
        total       : null,
        per_page    : Common.limitPageDefault,
        current_page: null,
      },
      index                 : 1,
      form                  : {
        selectCorporateName: "",
        billing_month : Common.billingMonthSelectionByMinMonth()[0]['key'],
        corporation_id: "",
        bill_method   : "",
        bill_via      : "",
        status        : [],
      },
      checkBoxBillingMethods: Common.billingMethods,
      checkBoxBillingVia    : Common.billingVia,
      selectedBillingMonth  : Common.billingMonthSelectionByMinMonth(Common.minBillingMonth),
      selectedCorporations  : [],
      sortKey               : 'billing_month',
      statusSort            : 'desc',
      columns               : {
        billing_month: {name: '請求月', sort: 'desc'},
        no           : {name: '伝票番号', sort: 'desc'},
        corporation  : {name: '法人名', sort: 'asc'},
        total_billed : {name: '請求額(税込)', sort: 'asc'},
        total_paid   : {name: '入金額', sort: 'asc'},
        status       : {name: '支払状況', sort: 'asc'},
        label        : {name: '支払方法', sort: 'asc'},
        bill_via     : {name: '請求書送付', sort: 'asc'},
      },
      bills                 : [],
      corporations          : [],
      errorMessages         : [],
      loadBillings          : false,
      paymentStatuses       : Common.paymentStatusesAdmin,
      corporationOptions  : [],
    }),
    created   : async function () {
      await this.getCorporationList();
      this.applyState();
    },
    computed  : {
      ...mapGetters('adminBillings', {
        formData : 'formData',
        sortData : 'sortData',
        isRefresh: 'isRefresh',
        isBack   : 'isBack',
      }),
    },
    methods   : {
      ...mapActions('adminBillings', [
        'saveFormData',
        'saveSortData',
        'saveRefresh',
        'saveBack',
      ]),
      applyState() {
        if ((this.isRefresh || this.isBack) && (this.formData !== null || this.sortData !== null)) {
          if (this.formData !== null) {
            this.pagination.current_page = this.formData.page;
            this.pagination.per_page     = this.formData.limit;
            this.form.billing_month      = this.formData.billing_month;
            this.form.bill_method        = this.formData.bill_method;
            this.form.bill_via           = this.formData.bill_via;
            this.form.corporation_id     = this.getCorporationOption(this.formData.corporation_id);
            this.form.status             = this.formData.status ?? [];
            this.selectedCorporations.push({
              corporation_id: this.form.corporation_id.id,
              name          : this.form.corporation_id.name,
            });
            this.form.selectCorporateName = this.getCorporationOption(this.formData.corporation_id);
          }
          if (this.sortData !== null) {
            this.sortKey    = this.sortData.sortKey;
            this.statusSort = this.sortData.statusSort;
          }
          this.searchSubmit();
        }
        this.saveRefresh(false);
        this.saveBack(false);
      },
      getCorporationOptions(searchTerm) {
        this.corporationOptions = new Promise(resolve => {
          if (!searchTerm) {
            return resolve(this.parseCorporations());
          }
          const term = searchTerm.toLowerCase()
          return resolve(this.parseCorporations().filter(({ name }) => name.toLowerCase().includes(term)))
        })
      },
      parseCorporations() {
        return this.selectedCorporations.map(corporation => ({
          'corporation_id': corporation.corporation_id,
          'name'          : corporation.uid + '：' + corporation.name,
          'toLowerCase'   : () => corporation.uid + '：' + corporation.name.toLowerCase(),
          'toString'      : () => corporation.uid + '：' + corporation.name,
        }));
      },
      async getCorporationList() {
        return apiClient.get(apiList.jon.corporations.corporations)
          .then(response => {
            if (this.hasResponseErrors(response)) {
              return;
            }

            this.selectedCorporations = response.data.corporations;
            this.getCorporationOptions();
          })
          .catch(error => {
            this.errorMessages = [error.message];
          })
          .finally(() => {
          });
      },
      setMetaPagination(meta) {
        this.pagination.total        = meta.total;
        this.pagination.per_page     = meta.per_page;
        this.pagination.current_page = meta.current_page;
      },
      jonBillingApi() {
        this.sending      = true;
        this.loadBillings = true;
        this.saveFormData({
          page          : this.pagination.current_page,
          limit         : this.pagination.per_page,
          billing_month : this.form.billing_month,
          bill_method   : this.form.bill_method,
          bill_via      : this.form.bill_via,
          corporation_id: this.form.corporation_id,
          status        : this.form.status,
        });
        this.saveSortData({
          sortKey   : this.sortKey,
          statusSort: this.statusSort,
        });
        apiClient.get(apiList.jon.billings.billings, {
          params: {
            page          : this.pagination.current_page,
            limit         : this.pagination.per_page,
            billing_month : this.form.billing_month,
            bill_method   : this.form.bill_method,
            bill_via      : this.form.bill_via,
            corporation_id: this.form.corporation_id.id,
            status        : this.form.status,
          }
        })
          .then(response => {
            if (this.hasResponseErrors(response)) {
              return;
            }

            const result = response.data;
            this.bills   = this.parseBillings(result.billings);
            this.setMetaPagination(result.meta)
          })
          .catch(error => {
            this.errorMessages = [error.message];
          })
          .finally(() => {
            this.sortBy(this.bills);
            this.sending      = false;
            this.loadBillings = false;
          });
      },
      onPageChanged(page) {
        this.pagination.current_page = page;
        this.jonBillingApi();
      },
      searchSubmit() {
        this.pagination.current_page = 1;
        this.jonBillingApi();
      },
      downloadAllInvoices() {
        this.sending = true;
        apiClient
          .post(apiList.jon.billings.downloadAllInvoices, {
            billing_month : this.form.billing_month,
            bill_method   : this.form.bill_method,
            corporation_id: this.form.corporation_id.id,
            status        : this.form.status,
          })
          .then(response => {
            if (response.data.errors) {
              this.errorMessages = [Common.messages.billings.downloadError];
              this.sending       = false;
              return;
            }
            const filename = 'invoices-' + this.form.billing_month + '.zip';
            this.downloadFile(response.data.path, filename);
          })
          .catch(error => {
            this.errorMessages = [Common.messages.billings.downloadError];
            this.sending       = false;
          })
          .finally(() => {
          });
      },
      downloadAllInvoicesByMail() {
        this.sending = true;
        apiClient
          .post(apiList.jon.billings.downloadAllInvoicesByMail, {
            billing_month : this.form.billing_month,
            bill_method   : this.form.bill_method,
            corporation_id: this.form.corporation_id.id,
            status        : this.form.status,
          })
          .then(response => {
            if (response.data.errors) {
              this.errorMessages = [Common.messages.billings.downloadError];
              this.sending       = false;
              return;
            }
            const filename = 'invoices-by-mail-' + this.form.billing_month + '.zip';
            this.downloadFile(response.data.path, filename);
          })
          .catch(error => {
            this.errorMessages = [Common.messages.billings.downloadError];
            this.sending       = false;
          })
          .finally(() => {
          });
      },
      downloadFile(path, filename) {
        this.sending = true;
        apiClient
          .request({
            url         : apiList.jon.billings.downloadFile,
            method      : 'post',
            responseType: 'arraybuffer',
            data        : {
              path: path,
            },
          })
          .then(response => {
            if (response.data) {
              const blob = new Blob([response.data], {type: 'application/zip'})
              saveAs(blob, filename);
            } else {
              this.errorMessages = [Common.messages.billings.downloadError];
            }
          })
          .catch(error => {
            this.errorMessages = [Common.messages.billings.downloadError];
          })
          .finally(() => {
            this.sending = false;
          });
      },
      parseBillings(billings) {
        const self  = this;
        const bills = [];
        billings.forEach(function (bill) {
          bill['label']       = bill.payment_method ? bill.payment_method.label : null;
          bill['bill_via']    = bill.payment_method ? bill.payment_method.bill_via : null;
          bill['corporation'] = self.getCorporationName(bill.corporation_id);
          bills.push(bill);
        });
        return bills;
      },
      getCorporationName(corporationId) {
        const corporation = this.selectedCorporations.filter(function (corporation) {
          return corporationId === corporation.corporation_id;
        });
        if (corporation && corporation.length > 0) {
          return corporation[0].name;
        }
        return null;
      },
      getPaymentInfo(billing) {
        if (!billing.payment_method) {
          return `<div class="md-table-cell-container"></div>`;
        }
        const billVia = this.getBillViaName(billing.payment_method.bill_via);
        const email   = billing.payment_method.email ? `<br/>${billing.payment_method.email}` : null;
        return `<div class="md-table-cell-container">${billVia}${email}</div>`;
      },
      getBillingMethod(billing) {
        if (!billing.payment_method) {
          return '';
        }
        return this.getBillMethodName(billing.payment_method.bill_method);
      },
      getCorporationOption(corporation) {
        if (!corporation) {
          return '';
        }
        const id = corporation.id ?? corporation.corporation_id ?? null;
        if (!id) {
          return '';
        }
        return {
          'id'         : id,
          'name'       : corporation.name,
          'toLowerCase': () => corporation.name.toLowerCase(),
          'toString'   : () => corporation.name,
        }
      },
    },
    watch: {
      'form.selectCorporateName': function () {
        this.form.corporation_id = this.getCorporationOption(this.form.selectCorporateName ?? {});
      },
    },
  }
</script>

<style lang="scss" scoped>
  .md-theme-default a:not(.md-button) {
    color: black;
  }

  a.active {
    font-weight: bold;
    color: black;
  }

  .text-right {
    text-align: right;
  }
</style>

<style lang="scss">
  @import "src/assets/scss/components.scss";
</style>
